import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { findUndeliveredMessage, ensureConversation } from './helpers';

export const mutations = {
  setConversations: (state, conversations) => {
    conversations.forEach(e => {
      const conversation = ensureConversation(state, e.display_id);
      state.details[String(e.display_id)] = {
        ...conversation,
        ...e,
      };
    });
  },

  setCurrentConversationId: (state, conversationId) => {
    state.currentConversationId = conversationId;
    if (conversationId) {
      ensureConversation(state, conversationId);
      state.details[conversationId].meta.unread_count = 0;
    }
  },

  setFetchingListStatus: (state, status) => {
    state.isFetchingList = status;
  },

  initConversation: (state, { conversationId }) => {
    ensureConversation(state, conversationId);
  },

  clearConversation: (state, { conversationId }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;
    conversation.messages = {};
  },

  pushMessageToConversation: (state, { conversationId, message }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    const { id, status, message_type: type } = message;
    const messageIdStr = String(id);

    const isMessageIncoming = type === MESSAGE_TYPE.INCOMING;
    const isTemporaryMessage = status === 'in_progress';

    if (!isMessageIncoming || isTemporaryMessage) {
      conversation.messages[messageIdStr] = message;
    } else {
      const [messageInConversation] = findUndeliveredMessage(
        Object.values(conversation.messages),
        message
      );

      if (!messageInConversation || !messageInConversation.id) {
        conversation.messages[messageIdStr] = message;
      } else {
        delete conversation.messages[String(messageInConversation.id)];
        conversation.messages[messageIdStr] = message;
      }
    }
  },

  updateAttachmentMessageStatus: (
    state,
    { conversationId, message, tempId }
  ) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;
    const tempIdStr = String(tempId);
    const messageIdStr = String(message.id);

    if (conversation.messages[tempIdStr]) {
      delete conversation.messages[tempIdStr];
      conversation.messages[messageIdStr] = { ...message };
    }
  },

  setConversationUIFlag: (state, payload) => {
    const { conversationId, ...uiFlags } = payload;
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    conversation.uiFlags = {
      ...conversation.uiFlags,
      ...uiFlags,
    };
  },

  setMessagesInConversation: (state, { conversationId, messages }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    conversation.uiFlags.allMessagesLoaded = true;

    const updatedMessages = {};
    messages.forEach(msg => {
      if (msg.id) {
        updatedMessages[String(msg.id)] = msg;
      }
    });

    conversation.messages = updatedMessages;
  },

  appendMessagesToConversation: (state, { conversationId, messages }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    const existingMessages = conversation.messages || {};

    messages.forEach(msg => {
      if (msg.id) {
        const msgIdStr = String(msg.id);
        if (!existingMessages[msgIdStr]) {
          existingMessages[msgIdStr] = msg;
        }
      }
    });

    conversation.messages = existingMessages;
  },

  updateMessage: (state, { conversationId, id, content_attributes }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;
    const messageIdStr = String(id);

    if (conversation.messages[messageIdStr]) {
      conversation.messages[messageIdStr] = {
        ...conversation.messages[messageIdStr],
        content_attributes: {
          ...(conversation.messages[messageIdStr].content_attributes || {}),
          ...content_attributes,
        },
      };
    }
  },

  updateMessageMeta: (state, { conversationId, id, meta }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;
    const messageIdStr = String(id);

    if (conversation.messages[messageIdStr]) {
      const currentMessage = conversation.messages[messageIdStr];
      const newMeta = currentMessage.meta
        ? { ...currentMessage.meta, ...meta }
        : { ...meta };
      conversation.messages[messageIdStr] = {
        ...currentMessage,
        meta: newMeta,
      };
    }
  },

  deleteMessage: (state, { conversationId, id }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;
    const messageIdStr = String(id);

    if (conversation.messages[messageIdStr]) {
      delete conversation.messages[messageIdStr];
    }
  },

  toggleAgentTypingStatus: (state, { conversationId, status }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;
    conversation.uiFlags.isAgentTyping = status === 'on';
  },

  toggleConnectAgentButton: (state, { conversationId, show }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;
    if (!conversation.uiFlags) {
      conversation.uiFlags = {};
    }
    conversation.uiFlags.showConnectAgent = show;
  },

  setMetaUserLastSeenAt: (state, { conversationId, lastSeen }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    if (!conversation.meta) {
      conversation.meta = {};
    }
    conversation.meta.contact_last_seen_at = lastSeen;
  },

  increaseMetaUnreadCount: (state, { conversationId }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    if (!conversation.meta) {
      conversation.meta = {};
    }
    conversation.meta.unread_count = (conversation.meta.unread_count || 0) + 1;
  },

  setLastMessageId: (state, { conversationId }) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    const messages = conversation.messages || {};

    if (!Object.keys(messages).length) return;

    const lastMessageKey =
      Object.keys(messages)[Object.keys(messages).length - 1];
    const lastMessage = messages[lastMessageKey];
    if (lastMessage && lastMessage.id) {
      conversation.lastMessageId = lastMessage.id;
      conversation.last_non_activity_message = lastMessage;
    }
  },

  updateConversationLastActivity: (
    state,
    { conversationId, lastActivityAt }
  ) => {
    const conversation = ensureConversation(state, conversationId);
    if (!conversation) return;

    if (!conversation.additional_attributes) {
      conversation.additional_attributes = {};
    }
    conversation.additional_attributes.last_activity_at = lastActivityAt;
  },

  removeConversation: (state, { conversationId }) => {
    const conversationIdStr = String(conversationId);
    if (state.details[conversationIdStr]) {
      delete state.details[conversationIdStr];
    }
    // 如果删除的是当前活跃的会话，清除当前会话ID
    if (state.currentConversationId === conversationIdStr) {
      state.currentConversationId = null;
    }
  },
};
